import axios from "axios";
import { createAuthHeaders } from "../api-client";
import { WorkspaceContextDto } from "@optidev/shared-types";
import type { ModelType } from "@/components/agent/ModelDropdown";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

export interface ModelSettingsResponse {
  defaultModel: ModelType | null;
}

export interface WorkspaceContext {
  industry?: string;
  size?: string;
  locations?: string[];
  description?: string;
  primaryUseCase?: string;
}

export const workspacesApi = {
  /**
   * Update workspace (admin only)
   * Can update: name, domain, industry, size
   */
  async updateWorkspace(
    token: string,
    workspaceId: string,
    updates: {
      name?: string;
      domain?: string;
      industry?: string;
      size?: string;
      metadata?: Record<string, any>;
    },
  ): Promise<{ id: string; name: string }> {
    const response = await axios.patch(
      `${API_URL}/api/workspaces/${workspaceId}`,
      updates,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Update workspace context (admin only)
   */
  async updateContext(
    token: string,
    workspaceId: string,
    context: WorkspaceContextDto,
  ): Promise<WorkspaceContext> {
    const response = await axios.patch(
      `${API_URL}/api/workspaces/${workspaceId}/context`,
      context,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Get chat sharing settings for the current workspace
   */
  async getChatSettings(
    token: string,
    workspaceId: string,
  ): Promise<{ shareAiChatHistory: boolean }> {
    const response = await axios.get(
      `${API_URL}/api/workspaces/chat-settings`,
      {
        headers: {
          ...createAuthHeaders(token),
          "X-Workspace-Id": workspaceId,
        },
      },
    );
    return response.data;
  },

  /**
   * Update chat sharing settings (admin only)
   */
  async updateChatSettings(
    token: string,
    workspaceId: string,
    settings: { shareAiChatHistory: boolean },
  ): Promise<{ shareAiChatHistory: boolean }> {
    const response = await axios.patch(
      `${API_URL}/api/workspaces/chat-settings`,
      settings,
      {
        headers: {
          ...createAuthHeaders(token),
          "X-Workspace-Id": workspaceId,
        },
      },
    );
    return response.data;
  },

  /**
   * Get default model settings for the current workspace
   */
  async getModelSettings(
    token: string,
    workspaceId: string,
  ): Promise<ModelSettingsResponse> {
    const response = await axios.get(
      `${API_URL}/api/workspaces/model-settings`,
      {
        headers: {
          ...createAuthHeaders(token),
          "X-Workspace-Id": workspaceId,
        },
      },
    );
    return response.data;
  },

  /**
   * Update default model settings (admin only)
   */
  async updateModelSettings(
    token: string,
    workspaceId: string,
    settings: { defaultModel: ModelType | null },
  ): Promise<ModelSettingsResponse> {
    const response = await axios.patch(
      `${API_URL}/api/workspaces/model-settings`,
      settings,
      {
        headers: {
          ...createAuthHeaders(token),
          "X-Workspace-Id": workspaceId,
        },
      },
    );
    return response.data;
  },
};
